import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksSchema } from "../machine-with-corrugate-tracks";
import { machineWorkstationSchema } from "../utils/machine-workstation";

const cvpRangeSettingsTrack = z.object({
	installed: z.boolean(),
	materialBrand: z.string().optional(),
	materialWidth: z.number().optional(),
	materialThickness: z.number().optional(),
	isEmpty: z.boolean(),
});

const cvpRangePlcSettings = z.object({
	track1: cvpRangeSettingsTrack.optional(),
	track2: cvpRangeSettingsTrack.optional(),
	track3: cvpRangeSettingsTrack.optional(),
	track4: cvpRangeSettingsTrack.optional(),
});

export const cvpRangeMachineSchema = machineWithCorrugateTracksSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.CVPRange),
	plcSettings: cvpRangePlcSettings.optional(),
	workstations: z.array(machineWorkstationSchema),
});

export const prepareCvpRangeMachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;

	const activeStopReason = input.stopReason as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof activeStopReason === "object" && activeStopReason !== null) {
		stopReason = {
			stopCode: (activeStopReason.code as number) ?? 0,
			message: (activeStopReason.message as string) ?? "",
			arguments: activeStopReason.contextValue === 0 ? [] : [activeStopReason.contextValue as number],
			isError: activeStopReason.isError === true,
		};
	}

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t, i) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			const assignedMaterial = track.assignedMaterial;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown> | undefined;
				return {
					assignedMaterial: {
						...assignedMaterial,
						brand: (material?.quality as number)?.toString() ?? "",
					},
					number: (track?.number as number) ?? i + 1,
					isEmpty: (track?.isTrackEmpty as boolean) ?? false,
				};
			}
		});
	}

	return {
		...input,
		iotDevice: {
			deviceId: input.iotDeviceId ?? "",
			deviceName: input.iotEdgeDeviceId ?? "",

			hubName: input.iotHubName ?? "",
			moduleId: (input.iotEdgeModuleId as string) ?? "N/A",
			moduleVersion: (input.moduleVersion as string) ?? "N/A",
			lastProofOfLife: input.lastProofOfLifeTimeStamp ?? null,
		},
		stopReason: stopReason,
		activeWarnings: [],
		tracks: tracks,
	};
};

export type CvpRangeMachine = z.infer<typeof cvpRangeMachineSchema>;
